<template>
  <div class="my-credit-card bg-white px-4 py-4">
    <div class="flex items-center justify-between flex-wrap gap-y-2 mb-4">
      <h5><i class="dot"></i>내 3S Credit <span class="year-label">({{ year }}년)</span></h5>
      <span v-if="심의대상" class="review-target">안전보건위원회 심의 대상</span>
    </div>

    <div v-if="loading" class="mb-4">
      <vs-progress indeterminate color="primary" />
    </div>

    <div v-else-if="error" class="empty-box">점수를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</div>

    <template v-else>
      <!-- 점수 요약 -->
      <div class="score-tiles mb-4">
        <div class="tile tile-plus">
          <div class="tile-label">가점 (세이프티 카드)</div>
          <div class="tile-value">{{ 가점 > 0 ? `+${가점}` : "-" }}</div>
        </div>
        <div class="tile tile-minus">
          <div class="tile-label">감점 (안전위반통보서)</div>
          <div class="tile-value">{{ 0 > 감점 ? 감점 : "-" }}</div>
        </div>
        <div class="tile tile-sum" :class="{ 'tile-warn': 심의대상 }">
          <div class="tile-label">합산</div>
          <div class="tile-value">{{ 합산 > 0 ? `+${합산}` : 합산 }}</div>
        </div>
      </div>

      <!-- 발급 내역 -->
      <div v-if="detailItems.length === 0" class="empty-box">{{ year }}년 발급 내역이 없습니다.</div>
      <div v-else class="table-scroll">
        <table class="inner-table">
          <colgroup>
            <col style="width: 90px" />
            <col style="width: 100px" />
            <col style="width: 110px" />
            <col style="width: 150px" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>구분</th>
              <th>발급일자</th>
              <th>등급</th>
              <th>유형</th>
              <th>세부내용</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in detailItems" :key="`credit-${index}`" :class="item.type === '가점' ? 'row-plus' : 'row-minus'">
              <td>{{ item.type }}</td>
              <td>{{ item.발급일자 }}</td>
              <td>
                <span class="grade-badge" :style="{ background: 등급뱃지색[item.등급] || '#999' }">{{ item.등급 }}</span>
                ({{ item.점수 > 0 ? `+${item.점수}` : item.점수 }}점)
              </td>
              <td>{{ item.유형 }}</td>
              <td class="text-left">{{ item.세부내용 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import HttpRequest from "@/util/HttpRequest";

// 프로젝트 기본화면 "내 3S Credit" 카드
// 1227 3S Credit 현황과 동일한 기준(승인 완료 건, 발급일자 기준 당해연도)으로 로그인 사용자(대상자) 본인의 점수만 보여준다
const SAFETY_CARD_BOARD_ID = 1228;
const VIOLATION_BOARD_ID = 1229;

const 가점점수 = { 개선: 1, 우수: 2, 탁월: 3 };
const 감점점수 = { 경미: -1, 주의: -2, 심각: -3 };

export default {
  name: "MyCreditCard",
  props: {
    projectId: { type: [String, Number], required: true },
  },
  data() {
    return {
      year: new Date().getFullYear(),
      loading: false,
      error: false,
      plusRawData: [],
      minusRawData: [],
      등급뱃지색: {
        개선: "#6da9e8",
        우수: "#e0b25a",
        탁월: "#e26b6b",
        경미: "#00CFE8",
        주의: "#FF9F43",
        심각: "#EA5455",
      },
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.auth.userInfo || {};
    },
    // 원시 데이터 → 본인 대상 발급 건 목록 (서버 조건으로 대상자 id 를 걸지만 사번 매칭까지 한 번 더 확인한다)
    items() {
      const items = [];
      const pushItem = (data, type) => {
        const content = data.postContents ? data.postContents : data.post_content ? JSON.parse(data.post_content) : {};
        const body = content.body || {};
        const 대상자 = body.대상자 || {};
        if (!this.isMine(대상자)) return;
        const 등급 = body.등급 || "";
        items.push({
          type,
          등급,
          점수: type === "가점" ? 가점점수[등급] || 0 : 감점점수[등급] || 0,
          발급일자: body.발급일자 || "",
          유형: type === "가점" ? body.발급유형 || "" : (body.위반내역 && body.위반내역.유형) || body.위반유형 || "",
          세부내용: type === "가점" ? body.세부설명 || "" : (body.위반내역 && body.위반내역.세부내용) || body.세부설명 || "",
        });
      };
      this.plusRawData.forEach((data) => pushItem(data, "가점"));
      this.minusRawData.forEach((data) => pushItem(data, "감점"));
      return items;
    },
    가점() {
      return this.items.filter((item) => item.type === "가점").reduce((sum, item) => sum + item.점수, 0);
    },
    감점() {
      return this.items.filter((item) => item.type === "감점").reduce((sum, item) => sum + item.점수, 0);
    },
    합산() {
      return this.가점 + this.감점;
    },
    // 1227 과 동일: 당해연도 합산 -3점 이하면 안전보건위원회 심의 대상
    심의대상() {
      return this.합산 <= -3;
    },
    detailItems() {
      return [...this.items].sort((a, b) => {
        return a.발급일자 < b.발급일자 ? 1 : -1;
      });
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    isMine(대상자) {
      const userInfo = this.userInfo;
      if (대상자.id && parseInt(대상자.id) === parseInt(userInfo.id)) return true;
      if (대상자.사번 && (대상자.사번 === userInfo.username || 대상자.사번 === userInfo.nickname)) return true;
      return false;
    },
    async load() {
      if (!this.projectId || !this.userInfo.id) return;
      this.loading = true;
      this.error = false;
      try {
        const [plus, minus] = await Promise.all([
          this.loadBoardAll(SAFETY_CARD_BOARD_ID, ["body.대상자%", "body.등급", "body.발급유형", "body.세부설명", "body.발급일자"]),
          this.loadBoardAll(VIOLATION_BOARD_ID, ["body.대상자%", "body.등급", "body.위반내역%", "body.세부설명", "body.발급일자"]),
        ]);
        this.plusRawData = plus;
        this.minusRawData = minus;
      } catch (e) {
        this.error = true;
      }
      this.loading = false;
    },
    // 결재 API(approval/all)는 본인이 관여(작성·결재·수신참조)한 문서만 돌려주므로 대상자 본인 조회에는 쓸 수 없다.
    // 대신 게시판 API 로 대상자 본인 문서를 조회한 뒤, 문서별 결재 상태(approval/document)를 확인해 승인(4) 건만 남긴다
    async loadBoardAll(boardId, metaKeys) {
      const baseOptions = {
        projectId: this.projectId,
        boardId,
        perPage: 200,
        conditions: [
          // 연도 귀속은 발급일자(작성자 입력) 기준 — 매년 1월 리셋
          { key: "body.발급일자", operator: "LIKE", value: `${this.year}-%` },
          { key: "body.대상자.id", operator: "=", value: `${this.userInfo.id}` },
        ],
        withMeta: true,
        onlyMeta: true,
        metaKeys,
      };

      const firstResponse = await this.$store.dispatch("board/LOAD_POST_LIST", { ...baseOptions, page: 1, cacheBuster: Date.now() });
      let allData = [...(firstResponse.data || [])];
      if (firstResponse.last_page > 1) {
        const pagePromises = [];
        for (let p = 2; p <= firstResponse.last_page; p++) {
          pagePromises.push(this.$store.dispatch("board/LOAD_POST_LIST", { ...baseOptions, page: p, cacheBuster: Date.now() }));
        }
        const remainingResponses = await Promise.all(pagePromises);
        for (const response of remainingResponses) {
          allData = allData.concat(response.data || []);
        }
      }

      // 결재 상태 확인: 승인(document_status_id 4) 건만 집계. 상태를 확인하지 못한 문서는 제외한다
      // approval/LOAD_POST 액션은 store.approval.postInfo 를 덮어써서 작성 화면에 영향을 줄 수 있으므로 API 를 직접 호출한다
      const statusChecks = allData.map(async (data) => {
        try {
          const document = await HttpRequest.get(`/api/v1/approval/document/board-${boardId}/${data.id}`);
          return parseInt(document && document.document_status_id) === 4 ? data : null;
        } catch (e) {
          return null;
        }
      });
      return (await Promise.all(statusChecks)).filter((data) => data !== null);
    },
  },
};
</script>

<style lang="scss" scoped>
.my-credit-card {
  min-height: 192px;
  box-shadow: 0 2px 8px 0 #00000021;
  border-radius: 8px;
  border: 1px solid #00000015;

  .year-label {
    font-size: 13px;
    font-weight: 400;
    color: #666;
  }
}

.review-target {
  color: #ea5455;
  font-weight: 600;
  font-size: 13px;
}

.score-tiles {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  .tile {
    border-radius: 8px;
    padding: 12px 16px;
    text-align: center;
    border: 1px solid #00000010;
  }
  .tile-label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }
  .tile-value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }
  .tile-plus {
    background: #eaf2fc;
    .tile-value {
      color: #2f6fba;
    }
  }
  .tile-minus {
    background: #fdecec;
    .tile-value {
      color: #c94545;
    }
  }
  .tile-sum {
    background: #fdf6ec;
    .tile-value {
      color: #353535;
    }
  }
  .tile-warn {
    border-color: #ea5455;
    .tile-value {
      color: #ea5455;
    }
  }
}

.empty-box {
  padding: 20px;
  text-align: center;
  font-size: 13px;
  color: #666;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
}

.table-scroll {
  overflow-x: auto;
}

.inner-table {
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #e5e5e5;
    padding: 6px 8px;
    font-size: 13px;
    text-align: center;
    vertical-align: middle;
    word-break: break-all;
  }
  th {
    background: #f5f5f5;
    font-weight: 600;
  }
  .text-left {
    text-align: left;
  }

  .grade-badge {
    display: inline-block;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    border-radius: 4px;
    padding: 2px 8px;
  }
  /* 행 배경은 위 가점/감점 타일과 동일한 색 */
  tr.row-plus td {
    background: #eaf2fc;
  }
  tr.row-minus td {
    background: #fdecec;
  }
}
</style>
